import 'shared-react/lumen/styles.css';

import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismComboSelectInput,
  PrismIcon,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-logo';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import { Illustration } from 'frontend-utils';
import HolidaysLogo1 from 'lumen/assets/images/logos/holidays-logo-1.svg?raw';
import HolidaysLogo2 from 'lumen/assets/images/logos/holidays-logo-2.svg?raw';
import HolidaysLogo3 from 'lumen/assets/images/logos/holidays-logo-3.svg?raw';
import TALogo from 'lumen/assets/images/white-label/TA-PoweredBy.svg?raw';
import TripadvisorLogo from 'lumen/assets/images/white-label/tripadvisor-logo.svg?raw';
import GeneralHeaderLogo from 'lumen/components/general/header-logo';
import TrophyModal from 'lumen/components/general/roi-trophies/trophy-modal';
import GeneralSmartInsightsSmartInsightsDrawer from 'lumen/components/general/smart-insights/smart-insights-drawer';
import GeneralSmartInsightsSmartInsightsNavigationButton from 'lumen/components/general/smart-insights/smart-insights-navigation-button';
import HotelAndMarketDropdownsHotelSelectionDropdown from 'lumen/components/hotel-and-market/dropdowns/hotel-selection-dropdown';
import HotelAndMarketDropdownsHotelSelectionDropdownOld from 'lumen/components/hotel-and-market/dropdowns/hotel-selection-dropdown-old';
import HotelAndMarketDropdownsTriggersAccountTrigger from 'lumen/components/hotel-and-market/dropdowns/triggers/account-trigger';
import HotelAndMarketModalsModalTrialIndicationSignUpToday from 'lumen/components/hotel-and-market/modals/modal-trial-indication-sign-up-today';
import HotelAndMarketNavigationNavigationMain from 'lumen/components/hotel-and-market/navigation/navigation-main';
import HotelAndMarketStaffAccessIndication from 'lumen/components/hotel-and-market/staff-access-indication';
import HotelAndMarketTrialIndication from 'lumen/components/hotel-and-market/trial-indication';
import type HotelController from 'lumen/controllers/hotel';
import hasFeatureToggle from 'lumen/helpers/has-feature-toggle';
import mountReact from 'lumen/modifiers/mount-react';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { HotelHeader, SmartInsights } from 'shared-react/lumen';

import styles from './hotel.module.css';

interface HotelSignature {
  Args: {
    controller: HotelController;
    model: unknown;
  };
}

<template>
  {{#if (hasFeatureToggle FEATURE_TOGGLES.REACT_HEADER)}}
    {{#if @controller.hotelAndMarketService.hotel}}
      <div
        {{mountReact
          HotelHeader
          (hash
            accountMenuOptions=@controller.accountMenuOptions
            accountSelectorOptions=@controller.accountSelectorOptions
            ernestButtonOptions=@controller.ernestButtonOptions
            hasSmartInsights=@controller.access.hasSmartInsights
            hotel=@controller.hotelAndMarketService.hotel
            smartInsightsButtonOptions=@controller.smartInsightsButtonOptions
            staffAccessOptions=@controller.staffAccessOptions
            trialOptions=@controller.trialOptions
          )
        }}
      ></div>
    {{/if}}
    {{#if @controller.isTrialModalVisible}}
      <HotelAndMarketModalsModalTrialIndicationSignUpToday
        @afterCloseAction={{@controller.closeTrialModal}}
      />
    {{/if}}
  {{else}}
    {{! template-lint-disable require-presentational-children }}
    <header class="header">
      <div class="content-wrapper flex justify-between">
        <div class="logo-and-rebranding-container">
          {{#if @controller.hotelOrMarket.isTripadvisorRebranding}}
            <div
              class="flex items-center"
              role="button"
              {{on "click" @controller.redirectToTripadvisorManagementConsole}}
            >
              <Illustration
                @html={{TripadvisorLogo}}
                class="platform-logo tripadvisor"
              />
            </div>
          {{else}}
            {{#if @controller.hotelOrMarket.hasRebranding}}
              <a class="flex items-center" href=".">
                <GeneralHeaderLogo
                  @reseller={{@controller.hotelOrMarket.reseller}}
                />
              </a>
            {{else if @controller.isHappyHolidays}}
              <a class="happy-holidays-logo" href={{@controller.hostDomain}}>
                <Illustration
                  @html={{HolidaysLogo1}}
                  class="holidays-logo holidays-logo-1"
                />
                <Illustration
                  @html={{HolidaysLogo2}}
                  class="holidays-logo holidays-logo-2"
                />
                <Illustration
                  @html={{HolidaysLogo3}}
                  class="holidays-logo holidays-logo-3"
                />
              </a>
            {{else}}
              <a class="flex items-center" href={{@controller.hostDomain}}>
                <Illustration
                  @html={{ICONS_LOGOS_MAP.logo-color}}
                  class="platform-logo"
                />
              </a>
            {{/if}}
          {{/if}}

          {{#if
            (and
              @controller.hotelOrMarket.hasRebranding
              @controller.hotelOrMarket.isTripadvisorRebranding
            )
          }}
            <Illustration @html={{TALogo}} class="poweredby--tripadvisor" />
          {{/if}}

          {{#if @controller.hasStaffAccessIndicator}}
            <HotelAndMarketStaffAccessIndication />
          {{/if}}

          <HotelAndMarketTrialIndication />
        </div>

        <HotelAndMarketNavigationNavigationMain />

        <div
          class="header-user-actions-container flex items-center justify-end"
        >
          <div
            class="flex items-center justify-center"
            data-event-id="hotel-or-market-search-dropdown"
            id="hotel-selecter-container"
          >
            {{#if (hasFeatureToggle FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW)}}
              <HotelAndMarketDropdownsHotelSelectionDropdown
                @isDemoMode={{@controller.isDemoMode}}
              />
            {{else}}
              <HotelAndMarketDropdownsHotelSelectionDropdownOld
                @isDemoMode={{@controller.isDemoMode}}
              />
            {{/if}}
          </div>

          {{#if @controller.showSmartInsights}}
            <GeneralSmartInsightsSmartInsightsNavigationButton
              @toggleSmartInsightsDrawer={{@controller.toggleSmartInsightsDrawer}}
            />
          {{/if}}

          <div
            class="settings-button ic-product-tour-settings-button flex items-center justify-center
              {{if @controller.isSettingsButtonActive 'active'}}"
            data-event-id="settings-button"
            data-test-id="settings-button"
            role="button"
            {{! @glint-expect-error: Incorrect type }}
            type="button"
            {{on "click" @controller.redirectToSettings}}
          >
            <PrismIcon @color="inherit" @name="settings" @size="large" />
          </div>
          <div
            class="account-button flex items-center justify-center
              {{if @controller.isAccountButtonActive 'active'}}"
          >
            <PrismComboSelectInput
              @horizontalPosition="right"
              @onChange={{@controller.performAction}}
              @onClose={{@controller.toggleActive}}
              @onOpen={{@controller.toggleActive}}
              @options={{@controller.settingsOptions}}
              @unsafe_dropdownClass="account-button-dropdown"
              @unsafe_triggerComponent={{HotelAndMarketDropdownsTriggersAccountTrigger}}
              @value={{null}}
              data-test-id="account-dropdown"
            />
          </div>
        </div>
      </div>
    </header>
  {{/if}}

  {{#if @controller.showEmptyState}}
    <div class="mt-800 flex justify-center">
      <PrismMessage
        @illustration="100x100_critical"
        @title={{t "general.permission_denied"}}
      />
    </div>
  {{else}}
    {{#if (hasFeatureToggle FEATURE_TOGGLES.SMART_INSIGHTS_REACT)}}
      <div class={{styles.smart-insights-row}}>
        <div class="main-content bg-main-content" id="content-container">
          {{! This is necessary to avoid https://github.com/emberjs/ember.js/issues/14708 when doing unload on store }}
          {{#unless @controller.hotelAndMarketService.loading}}
            {{outlet}}
          {{/unless}}
        </div>

        {{#if @controller.showSmartInsights}}
          <div
            class={{styles.smart-insights-react-mount}}
            {{didInsert @controller.handleReactSmartInsightsMountInsert}}
            {{mountReact SmartInsights @controller.reactSmartInsightsProps}}
          ></div>
        {{/if}}
      </div>
    {{else}}
      {{#if @controller.showSmartInsights}}
        <GeneralSmartInsightsSmartInsightsDrawer
          @selectedPriorities={{@controller.priorities}}
          @selectedSmartInsight={{@controller.selectedSmartInsight}}
          @setSelectedPriorities={{@controller.setSmartInsightsPriorities}}
          @setSmartInsightsDay={{@controller.setSmartInsightsDay}}
          @setSmartInsightsKpiComparisonPeriod={{@controller.setSmartInsightsKpiComparisonPeriod}}
          @setSmartInsightsTab={{@controller.setSmartInsightsTab}}
          @setSmartInsightsView={{@controller.setSmartInsightsView}}
          @smartInsightsDay={{@controller.smartInsightsDay}}
          @smartInsightsKpiComparisonPeriod={{@controller.smartInsightsKpiComparisonPeriod}}
          @smartInsightsTab={{@controller.smartInsightsTab}}
          @smartInsightsView={{@controller.smartInsightsView}}
          @toggleSmartInsightsDrawer={{@controller.toggleSmartInsightsDrawer}}
        />
      {{/if}}

      <div class="main-content bg-main-content" id="content-container">
        {{! This is necessary to avoid https://github.com/emberjs/ember.js/issues/14708 when doing unload on store }}
        {{#unless @controller.hotelAndMarketService.loading}}
          {{outlet}}
        {{/unless}}
      </div>
    {{/if}}
  {{/if}}

  {{#if
    (and
      @controller.access.hasTrophyHighlights
      @controller.roiTrophies.weeklyHighlightTrophy
    )
  }}
    <TrophyModal
      @activeTrophy={{@controller.roiTrophies.weeklyHighlightTrophy}}
      @onDismiss={{@controller.roiTrophies.dismissHighlightTask.perform}}
    />
  {{/if}}

  {{! the following div is only used by the people at Tripadvisor to test if the iframe loads correctly. Do not remove it.}}
  <div hidden id="ta-page-load-test"></div>

  <form
    action={{@controller.intercom.tripadvisorHelpRedirect}}
    hidden
    method="post"
    name="intercom-form"
    target="_blank"
  >
    <input
      name="access_token"
      type="hidden"
      value={{@controller.intercom.intercomSessionId}}
    />
    <input
      name="article_path"
      type="hidden"
      value={{@controller.intercom.intercomHelpArticleLink}}
    />
  </form>
</template> satisfies TOC<HotelSignature>;
